/**
 * Version Check Hook
 *
 * Detects app version changes and manages storage clearing
 * Coordinates updates across multiple tabs
 * Provides options for auto-refresh and user notifications
 */

import { useEffect, useRef, useState } from 'react';

import { queryClient } from '@/queryclient';
import {
  clearStorageOnVersionChange,
  getCurrentVersion,
  getStoredVersion,
  notifyVersionChange,
  updateStoredVersion,
  versionBroadcast,
  VersionInfo,
} from '@/utils/version';

/**
 * Configuration options for version checking behavior
 */
export interface UseVersionCheckOptions {
  /**
   * Callback invoked when version change is detected
   * @param oldVersion - Previous version string
   * @param newVersion - Current version string
   */
  onVersionChange?: (oldVersion: string, newVersion: string) => void;

  /**
   * Enable automatic page refresh on version change (recommended for production)
   * Default: false
   */
  enableAutoRefresh?: boolean;

  /**
   * Show user notification on version change
   * Default: true
   */
  notifyUser?: boolean;
}

/**
 * Return type for useVersionCheck hook
 */
export interface UseVersionCheckResult {
  /**
   * Current version information
   */
  versionInfo: VersionInfo;

  /**
   * Whether the current version is outdated (detected version change)
   */
  isVersionOutdated: boolean;

  /**
   * Force a page refresh (manual trigger)
   */
  forceRefresh: () => void;
}

/**
 * useVersionCheck Hook
 *
 * Performs version checking once on component mount
 * Handles storage clearing, cache invalidation, and multi-tab coordination
 *
 * @param options - Configuration options
 * @returns Version check state and utilities
 *
 * @example
 * ```tsx
 * function App() {
 *   const { isVersionOutdated, forceRefresh } = useVersionCheck({
 *     enableAutoRefresh: import.meta.env.PROD,
 *     onVersionChange: (oldVer, newVer) => {
 *       console.log(`Updated from ${oldVer} to ${newVer}`);
 *     },
 *   });
 *
 *   return <YourApp />;
 * }
 * ```
 */
export function useVersionCheck(
  options: UseVersionCheckOptions = {}
): UseVersionCheckResult {
  const {
    onVersionChange,
    enableAutoRefresh = false,
    notifyUser = true,
  } = options;

  // Ensure version check only runs once
  const hasCheckedRef = useRef(false);

  // State for version tracking
  const [isVersionOutdated, setIsVersionOutdated] = useState(false);
  const [versionInfo, setVersionInfo] = useState<VersionInfo>(() =>
    getCurrentVersion()
  );

  useEffect(() => {
    // Only run once on mount
    if (hasCheckedRef.current) {
      return;
    }
    hasCheckedRef.current = true;

    // Initialize broadcast service
    versionBroadcast.initialize();

    // Get current and stored versions
    const currentVersion = getCurrentVersion();
    const storedVersion = getStoredVersion();

    // First-time user or no stored version
    if (!storedVersion) {
      updateStoredVersion(currentVersion);
      setVersionInfo(currentVersion);
      return;
    }

    // Check if version has changed
    const versionChanged =
      storedVersion.version !== currentVersion.version ||
      storedVersion.buildTimestamp !== currentVersion.buildTimestamp ||
      storedVersion.gitCommit !== currentVersion.gitCommit;

    if (versionChanged) {
      // Clear selective storage (preserves user preferences)
      clearStorageOnVersionChange();

      // Clear React Query cache
      queryClient.clear();

      // Update stored version
      updateStoredVersion(currentVersion);

      // Notify other tabs about version change
      notifyVersionChange(currentVersion.version);

      // Notify parent component
      if (onVersionChange) {
        onVersionChange(storedVersion.version, currentVersion.version);
      }

      // Mark version as outdated
      setIsVersionOutdated(true);

      // Auto-refresh if enabled (production mode)
      if (enableAutoRefresh) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } else {
      // Same version - just update last checked timestamp
      updateStoredVersion(currentVersion);
    }

    setVersionInfo(currentVersion);

    // Listen for version changes from other tabs
    const cleanup = versionBroadcast.onVersionChange(_message => {
      // Reload this tab after short delay to allow other tabs to coordinate
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    });

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [onVersionChange, enableAutoRefresh, notifyUser]);

  /**
   * Force a page refresh (manual trigger)
   */
  const forceRefresh = () => {
    window.location.reload();
  };

  return {
    versionInfo,
    isVersionOutdated,
    forceRefresh,
  };
}
